import { useMemo } from "react";
import { useInsightState } from "./context";
import { InsightStatus } from "./constants";
import { getOpportunityStatus } from "@/pages/explorer/utils";
import { GetOpportunitiesDataResponse } from "@/pages/explorer/types";

/**
 * Filters and sorts opportunities based on the status filter and sort
 * preferences from InsightContext. Used by QueryDetails and GroupDetails
 * to process sqlLineage tags before passing to InsightSection.
 */
export const useFilteredOpportunities = (
  opportunities: GetOpportunitiesDataResponse[]
): GetOpportunitiesDataResponse[] => {
  const { selectedTab: statusFilter, sort } = useInsightState();

  return useMemo(() => {
    // Filter by status
    const filtered = opportunities.filter(o => {
      if (!statusFilter || statusFilter.length === 0) return true;
      const status = getOpportunityStatus(o.status);
      return statusFilter.includes(status as InsightStatus);
    });
    // Sort
    if (sort.sortBy && sort.sortOrder) {
      const { sortBy, sortOrder } = sort;
      const direction = sortOrder === "asc" ? 1 : -1;
      return [...filtered].sort((a, b) => {
        let aVal: number;
        let bVal: number;
        if (sortBy === "effort") {
          aVal = a.effort;
          bVal = b.effort;
        } else if (sortBy === "cost_savings_max") {
          const aRec = a as Record<string, unknown>;
          const bRec = b as Record<string, unknown>;
          aVal = Number(aRec["cost_savings_max"]) || Number(a.money_savings?.max) || 0;
          bVal = Number(bRec["cost_savings_max"]) || Number(b.money_savings?.max) || 0;
        } else {
          return 0;
        }
        return (aVal - bVal) * direction;
      });
    }
    // No explicit sort: keep a deterministic alphabetical order by name
    return [...filtered].sort((a, b) => a.opportunity_name.localeCompare(b.opportunity_name));
  }, [opportunities, statusFilter, sort]);
};
